import tokenService from './services/tokenService';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { EnhancedNavLink, EnhancedButtonLink } from './components/EnhancedNavLink';
import VoxSmythIcons, { VoxSmythAnimations } from './VoxSmythIcons';
import VoxSmythUtils from './utils/VoxSmythUtils';
import { openCookiePreferences } from './components/CookieConsentBanner';

// Enhanced Global Footer - "Literary Tech with Whisper of Magic" themed
const EnhancedFooter = ({ user, onNavigatePricing, onLogout }) => {
  const location = useLocation();
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (i) => setOpenSections(s => ({ ...s, [i]: !s[i] }));

  // Enhanced Design Tokens for Footer
  const FOOTER_TOKENS = {
    colors: {
      // Core Palette
      footer_bg: '#1A202C',
      section_bg: '#2C3A4C', 
      accent_brass: '#D7B377',
      warm_parchment: '#F9F6F0',
      text_primary: '#FFFFFF',
      text_secondary: 'rgba(255, 255, 255, 0.8)',
      text_muted: 'rgba(255, 255, 255, 0.5)',
      border_subtle: 'rgba(215, 179, 119, 0.2)',
      border_mystical: 'rgba(215, 179, 119, 0.3)',
      hover_brass: '#E2D7C4',
      mystical_glow: 'rgba(215, 179, 119, 0.1)'
    },
    typography: {
      brand_title: 'DM Serif Display, Georgia, serif',
      section_title: 'DM Sans, sans-serif',
      link_text: 'Inter, sans-serif',
      copyright: 'Inter, sans-serif',
      mystical_quote: 'DM Serif Display, Georgia, serif'
    },
    spacing: {
      section_gap: '48px',
      link_gap: '12px',
      padding_main: '64px 24px 32px'
    }
  };

  const handleScrollToTop = () => {
    VoxSmythUtils.scrollToTop();
  };

  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Discover',
      icon: VoxSmythIcons.Library,
      links: [
        { name: 'Literary Bookstore', path: '/emporium', icon: VoxSmythIcons.Earnings },
        { name: 'Voice Library', path: '/voices', icon: VoxSmythIcons.VoiceStudio },
        { name: 'Book Library', path: '/library', icon: VoxSmythIcons.Library },
        { name: 'Creator Studio', path: '/creator', icon: VoxSmythIcons.Quill },
        { name: 'Documentation', path: '/docs', icon: VoxSmythIcons.BookThumbnail }
      ]
    },
    {
      title: 'Community',
      icon: VoxSmythIcons.User,
      links: [
        { name: 'Referral Program', path: '/referral', icon: VoxSmythIcons.MagicalSparkle },
        { name: 'Voice Actors', path: (user && (user.tier?.toLowerCase() === 'voice_actor' || user.tier?.toLowerCase() === 'creator')) ? '/voice-actor-portal' : '/coming-soon', icon: VoxSmythIcons.VintageMicrophone },
        { name: 'Help Center', path: '/docs', icon: VoxSmythIcons.Search },
        { name: 'FAQ', path: '/faq', icon: VoxSmythIcons.Info },
        { name: 'Marketplace', path: '/marketplace', icon: VoxSmythIcons.VintageMicrophone },
        { name: 'Contact Support', path: '/contact', icon: VoxSmythIcons.User }
      ]
    },
    {
      title: 'Account',
      icon: VoxSmythIcons.Settings,
      links: [
        { name: 'Billing & Plans', path: '/pricing', icon: VoxSmythIcons.Earnings },
        { name: 'Settings', path: '/account', icon: VoxSmythIcons.Settings, requiresAuth: true },
        { name: user ? 'Sign Out' : 'Sign In', path: user ? '/logout' : '/auth', icon: user ? VoxSmythIcons.ArrowLeft : VoxSmythIcons.User }
      ]
    },
    {
      title: 'Legal',
      icon: VoxSmythIcons.BookThumbnail,
      links: [
        { name: 'Privacy Policy', path: '/privacy-policy', icon: VoxSmythIcons.BookThumbnail },
        { name: 'Terms of Use', path: '/terms-of-use', icon: VoxSmythIcons.BookThumbnail },
        { name: 'Acceptable Use', path: '/acceptable-use-policy', icon: VoxSmythIcons.BookThumbnail },
        { name: 'Cookie Policy', path: '/cookie-policy', icon: VoxSmythIcons.BookThumbnail },
        { name: 'Cookie Preferences', action: 'cookie-prefs', icon: VoxSmythIcons.Settings },
        { name: 'Data Deletion', path: '/data-deletion', icon: VoxSmythIcons.BookThumbnail }
      ]
    },
    {
      title: 'Compare',
      icon: VoxSmythIcons.Analytics,
      links: [
        { name: 'Audible Alternatives', staticPage: '/audible-alternative/', icon: VoxSmythIcons.MagicalSparkle },
        { name: 'VoxSmyth vs Audible', staticPage: '/vs/audible/', icon: VoxSmythIcons.BookThumbnail },
        { name: 'VoxSmyth vs Spotify', staticPage: '/vs/spotify-audiobooks/', icon: VoxSmythIcons.BookThumbnail },
        { name: 'VoxSmyth vs Libby', staticPage: '/vs/libby/', icon: VoxSmythIcons.BookThumbnail },
        { name: 'Free Classic Audiobooks', staticPage: '/books/', icon: VoxSmythIcons.Library }
      ]
    }
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: VoxSmythIcons.User, url: 'https://linkedin.com/company/voxsmyth', external: true },
    { name: 'Instagram', icon: VoxSmythIcons.MagicalSparkle, url: 'https://instagram.com/voxsmyth', external: true },
    { name: 'X (Twitter)', icon: VoxSmythIcons.Analytics, url: 'https://twitter.com/voxsmyth', external: true },
    { name: 'Discord', icon: VoxSmythIcons.VoiceStudio, url: 'https://discord.gg/voxsmyth', external: true }
  ];

  const handleLinkClick = (link) => {
    if (link.action === 'cookie-prefs') {
      openCookiePreferences();
      return;
    }
    if (link.staticPage) {
      // Static (non-SPA) SEO pages — full navigation
      window.location.href = link.staticPage;
      return;
    }
    if (link.external) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // For Sign Out, handle logout properly
    if (link.name === 'Sign Out' && onLogout) {
      onLogout();
      return;
    }
    
    // Fallback logout if onLogout not provided (backward compatibility)
    if (link.name === 'Sign Out') {
      tokenService.clearToken();
      window.location.href = '/';
      return;
    }
    
    // All other links will be handled by React Router Link component
    // No additional action needed here
  };

  // Don't show footer on certain pages
  const hideFooterPaths = ['/player', '/admin'];
  const shouldHideFooter = hideFooterPaths.some(path => location.pathname.startsWith(path));

  if (shouldHideFooter) {
    return null;
  }

  return (
    <footer 
      className="enhanced-footer"
      style={{
        background: `linear-gradient(135deg, ${FOOTER_TOKENS.colors.footer_bg} 0%, ${FOOTER_TOKENS.colors.section_bg} 100%)`,
        color: FOOTER_TOKENS.colors.text_primary,
        marginTop: 'auto',
        position: 'relative',
        overflow: 'hidden',
        contain: 'layout',
      }}
    >
      {/* Global Styles - Fixed JSX attribute issue */}
      <style>{`
        ${VoxSmythAnimations}
        
        .enhanced-footer .footer-link:hover {
          color: ${FOOTER_TOKENS.colors.accent_brass} !important;
          transform: translateX(4px);
        }
        
        .enhanced-footer .footer-link:hover .link-icon {
          color: ${FOOTER_TOKENS.colors.accent_brass} !important;
          transform: scale(1.1);
        }
        
        .enhanced-footer .social-link:hover {
          background: ${FOOTER_TOKENS.colors.accent_brass}20 !important;
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 4px 16px ${FOOTER_TOKENS.colors.mystical_glow} !important;
        }

        .enhanced-footer .social-link:hover .social-tooltip {
          opacity: 1 !important;
          visibility: visible !important;
          transform: translateX(-50%) translateY(-4px);
        }
        
        .enhanced-footer .scroll-top-btn:hover {
          background: ${FOOTER_TOKENS.colors.accent_brass} !important;
          transform: translateY(-3px) scale(1.1);
          box-shadow: 0 8px 24px ${FOOTER_TOKENS.colors.accent_brass}30 !important;
        }
        
        .enhanced-footer .section-title-icon:hover {
          animation: floating-sparkle 2s ease-in-out infinite;
        }
        
        .enhanced-footer .mystical-border::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, ${FOOTER_TOKENS.colors.accent_brass}, transparent);
          animation: brass-shimmer 3s ease-in-out infinite;
        }
        
        @keyframes footer-glow {
          0%, 100% { 
            box-shadow: 0 0 20px ${FOOTER_TOKENS.colors.mystical_glow}; 
          }
          50% { 
            box-shadow: 0 0 40px ${FOOTER_TOKENS.colors.accent_brass}20; 
          }
        }
        
        {/* Collapsible link lists — always expanded on desktop, accordion on mobile */}
        .enhanced-footer .footer-links-list {
          overflow: hidden;
        }
        .enhanced-footer .footer-section-chevron {
          display: none;
          transition: transform 0.3s ease;
        }
        .enhanced-footer .footer-section-header {
          background: none;
          border: none;
          padding: 0;
          cursor: default;
          width: 100%;
          text-align: left;
        }

        @media (max-width: 768px) {
          .enhanced-footer .footer-sections {
            grid-template-columns: 1fr !important;
            gap: 4px !important;
          }
          
          .enhanced-footer .footer-section {
            margin-bottom: 0 !important;
            border-bottom: 1px solid ${FOOTER_TOKENS.colors.border_subtle};
          }

          .enhanced-footer .footer-section-header {
            cursor: pointer;
            padding: 14px 0 !important;
            margin-bottom: 0 !important;
          }

          .enhanced-footer .footer-section-chevron {
            display: block;
            margin-left: auto;
          }
          .enhanced-footer .footer-section-header[aria-expanded="true"] .footer-section-chevron {
            transform: rotate(180deg);
          }

          .enhanced-footer .footer-links-list {
            max-height: 0;
            transition: max-height 0.35s ease;
          }
          .enhanced-footer .footer-links-list.open {
            max-height: 400px;
            padding-bottom: 12px;
          }
          
          .enhanced-footer .footer-brand {
            text-align: center !important;
            margin-bottom: 24px !important;
          }
        }
        
        @media (max-width: 480px) {
          .enhanced-footer .footer-header {
            flex-direction: column !important;
            align-items: center !important;
            text-align: center !important;
            gap: 24px !important;
          }
        }
      `}</style>

      {/* Mystical Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, ${FOOTER_TOKENS.colors.mystical_glow} 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, ${FOOTER_TOKENS.colors.mystical_glow} 0%, transparent 50%),
          linear-gradient(135deg, transparent 40%, ${FOOTER_TOKENS.colors.mystical_glow} 50%, transparent 60%)
        `,
        opacity: 0.3,
        pointerEvents: 'none'
      }} />

      {/* Main Footer Content */}
      <div style={{ 
        maxWidth: '1200px',
        margin: '0 auto',
        padding: FOOTER_TOKENS.spacing.padding_main,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Footer Header */}
        <div 
          className="footer-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '36px',
            flexWrap: 'wrap',
            gap: '24px'
          }}
        >
          {/* Brand Section */}
          <div className="footer-brand" style={{ maxWidth: '400px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${FOOTER_TOKENS.colors.accent_brass} 0%, ${FOOTER_TOKENS.colors.hover_brass} 100%)`,
                boxShadow: `0 4px 16px ${FOOTER_TOKENS.colors.accent_brass}30`
              }}>
                <VoxSmythIcons.VoxSmythSigil 
                  size={24} 
                  color={FOOTER_TOKENS.colors.footer_bg}
                  animated={true}
                />
              </div>
              <h2 style={{
                fontFamily: FOOTER_TOKENS.typography.brand_title,
                fontSize: '32px',
                color: FOOTER_TOKENS.colors.text_primary,
                margin: 0,
                textShadow: `0 2px 8px ${FOOTER_TOKENS.colors.mystical_glow}`
              }}>
                VoxSmyth
              </h2>
            </div>
            <p style={{
              fontFamily: FOOTER_TOKENS.typography.link_text,
              fontSize: '14px',
              lineHeight: '1.6',
              color: FOOTER_TOKENS.colors.text_secondary,
              margin: '0 0 18px 0'
            }}>
              Transform any written story into a captivating multi-voice audiobook experience. 
              Where AI meets the art of storytelling, and every character finds their perfect voice.
            </p>
            
            {/* Social Links */}
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <button
                    key={index}
                    className="social-link"
                    onClick={() => handleLinkClick(social)}
                    title={`Follow us on ${social.name}`}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: `rgba(255, 255, 255, 0.08)`,
                      border: `1px solid ${FOOTER_TOKENS.colors.border_subtle}`,
                      color: FOOTER_TOKENS.colors.text_secondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      backdropFilter: 'blur(10px)',
                      position: 'relative'
                    }}
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <IconComponent size={20} color={FOOTER_TOKENS.colors.text_secondary} />
                    
                    {/* Tooltip */}
                    <div style={{
                      position: 'absolute',
                      bottom: '60px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: FOOTER_TOKENS.colors.section_bg,
                      color: FOOTER_TOKENS.colors.text_primary,
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontFamily: FOOTER_TOKENS.typography.link_text,
                      whiteSpace: 'nowrap',
                      opacity: 0,
                      visibility: 'hidden',
                      transition: 'all 0.2s ease',
                      pointerEvents: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                      border: `1px solid ${FOOTER_TOKENS.colors.border_mystical}`
                    }} className="social-tooltip">
                      {social.name}
                      
                      {/* Arrow */}
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '4px solid transparent',
                        borderRight: '4px solid transparent',
                        borderTop: `4px solid ${FOOTER_TOKENS.colors.section_bg}`
                      }} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scroll to Top Button */}
          <button
            className="scroll-top-btn"
            onClick={handleScrollToTop}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: `rgba(255, 255, 255, 0.1)`,
              border: `1px solid ${FOOTER_TOKENS.colors.border_mystical}`,
              color: FOOTER_TOKENS.colors.text_primary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              backdropFilter: 'blur(10px)'
            }}
            aria-label="Scroll to top"
          >
            <VoxSmythIcons.ArrowUp size={24} color={FOOTER_TOKENS.colors.text_primary} />
          </button>
        </div>

        {/* Footer Sections */}
        <div 
          className="footer-sections"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: FOOTER_TOKENS.spacing.section_gap,
            marginBottom: '36px'
          }}
        >
          {footerSections.map((section, sectionIndex) => {
            const SectionIcon = section.icon;
            return (
              <div key={sectionIndex} className="footer-section">
                <button
                  type="button"
                  className="footer-section-header"
                  data-testid={`footer-section-toggle-${section.title.toLowerCase()}`}
                  aria-expanded={!!openSections[sectionIndex]}
                  onClick={() => toggleSection(sectionIndex)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '18px'
                  }}
                >
                  <div className="section-title-icon">
                    <SectionIcon 
                      size={20} 
                      color={FOOTER_TOKENS.colors.accent_brass}
                    />
                  </div>
                  <h3 style={{
                    fontFamily: FOOTER_TOKENS.typography.section_title,
                    fontSize: '16px',
                    fontWeight: '600',
                    color: FOOTER_TOKENS.colors.text_primary,
                    margin: 0,
                    letterSpacing: '0.5px'
                  }}>
                    {section.title}
                  </h3>
                  <svg className="footer-section-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={FOOTER_TOKENS.colors.accent_brass} strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <ul className={`footer-links-list${openSections[sectionIndex] ? ' open' : ''}`} style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0
                }}>
                  {section.links.map((link, linkIndex) => {
                    const LinkIcon = link.icon;
                    return (
                      <li key={linkIndex} style={{ marginBottom: FOOTER_TOKENS.spacing.link_gap }}>
                        {link.external || link.name === 'Sign Out' || link.action || link.staticPage ? (
                          <button
                            className="footer-link"
                            data-testid={link.action === 'cookie-prefs' ? 'footer-cookie-prefs' : undefined}
                            onClick={() => handleLinkClick(link)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: FOOTER_TOKENS.colors.text_secondary,
                              cursor: 'pointer',
                              fontFamily: FOOTER_TOKENS.typography.link_text,
                              fontSize: '14px',
                              padding: '6px 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                              width: '100%',
                              textAlign: 'left'
                            }}
                          >
                            <span 
                              className="link-icon"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '20px',
                                height: '20px',
                                transition: 'all 0.3s ease',
                                flexShrink: 0
                              }}
                            >
                              <LinkIcon size={16} color={FOOTER_TOKENS.colors.text_secondary} />
                            </span>
                            <span style={{ flex: 1 }}>{link.name}</span>
                            {link.external && (
                              <VoxSmythIcons.ArrowRight 
                                size={12} 
                                color={FOOTER_TOKENS.colors.text_muted}
                                style={{ transform: 'rotate(-45deg)' }}
                              />
                            )}
                          </button>
                        ) : (
                          <EnhancedNavLink
                            to={link.requiresAuth && !user && link.path !== '/auth' ? '/auth' : link.path}
                            className="footer-link"
                            style={{
                              color: FOOTER_TOKENS.colors.text_secondary,
                              fontFamily: FOOTER_TOKENS.typography.link_text,
                              fontSize: '14px',
                              padding: '6px 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                              width: '100%'
                            }}
                          >
                            <span 
                              className="link-icon"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '20px',
                                height: '20px',
                                transition: 'all 0.3s ease',
                                flexShrink: 0
                              }}
                            >
                              <LinkIcon size={16} color={FOOTER_TOKENS.colors.text_secondary} />
                            </span>
                            <span style={{ flex: 1 }}>{link.name}</span>
                          </EnhancedNavLink>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer Bottom */}
        <div style={{
          borderTop: `1px solid ${FOOTER_TOKENS.colors.border_mystical}`,
          paddingTop: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '24px'
        }}>
          <p style={{
            fontFamily: FOOTER_TOKENS.typography.copyright,
            fontSize: '14px',
            color: FOOTER_TOKENS.colors.text_muted,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>© {currentYear} VoxSmyth. All rights reserved.</span>
            <VoxSmythIcons.MagicalSparkle 
              size={12} 
              color={FOOTER_TOKENS.colors.accent_brass}
            />
            <span style={{ color: FOOTER_TOKENS.colors.accent_brass }}>
              Crafted for storytellers worldwide.
            </span>
            {/* Admin Portal Link */}
            <span style={{ color: FOOTER_TOKENS.colors.text_muted, margin: '0 8px' }}>•</span>
            <EnhancedNavLink 
              to="/admin-portal" 
              style={{
                fontFamily: FOOTER_TOKENS.typography.copyright,
                fontSize: '14px',
                color: FOOTER_TOKENS.colors.text_muted,
                textDecoration: 'none'
              }}
            >
              Admin Portal
            </EnhancedNavLink>
          </p>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '13px',
            color: FOOTER_TOKENS.colors.text_muted
          }}>
            <span>Made with</span>
            <VoxSmythIcons.Quill 
              size={14} 
              color={FOOTER_TOKENS.colors.accent_brass}
              animated={true}
            />
            <span>and</span>
            <VoxSmythIcons.VintageMicrophone 
              size={14} 
              color={FOOTER_TOKENS.colors.accent_brass}
              animated={true}
            />
            <span>for the love of stories</span>
          </div>
        </div>
      </div>

      {/* Enhanced Mystical Bottom Border */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, 
          transparent 0%, 
          ${FOOTER_TOKENS.colors.accent_brass}30 25%, 
          ${FOOTER_TOKENS.colors.accent_brass} 50%, 
          ${FOOTER_TOKENS.colors.accent_brass}30 75%, 
          transparent 100%)`,
        opacity: 0.6
      }} />
    </footer>
  );
};

export default EnhancedFooter;